import { PillMarquee, type ContentType, type PillMarqueeItem } from './pill-marquee';

const IncreasingBoxArrow = () => (
  <svg width="27" height="26" viewBox="0 0 27 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_415_24189)">
      <g clipPath="url(#clip1_415_24189)">
        <path
          d="M3.2687 25.5277C2.03709 25.5277 1.03906 25.5277 1.03906 25.5277V0.683105C1.03906 0.683105 2.03709 0.683105 3.2687 0.683105H23.654C24.8856 0.683105 25.8836 0.683105 25.8836 0.683105V25.5277C25.8836 25.5277 24.8856 25.5277 23.654 25.5277H3.2687Z"
          fill="#FF9473"
        />
        <path
          d="M3.2687 25.5277C2.03709 25.5277 1.03906 25.5277 1.03906 25.5277V0.683105C1.03906 0.683105 2.03709 0.683105 3.2687 0.683105H23.654C24.8856 0.683105 25.8836 0.683105 25.8836 0.683105V25.5277C25.8836 25.5277 24.8856 25.5277 23.654 25.5277H3.2687Z"
          stroke="#FF9473"
          strokeWidth="0.63704"
          strokeMiterlimit="7.5"
        />
        <path
          d="M13.4414 17.2369V8.99701M13.4414 8.99701L15.66 11.2156M13.4414 8.99701L11.2236 11.2156"
          stroke="#183344"
          strokeWidth="1.69877"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </g>
    <defs>
      <clipPath id="clip0_415_24189">
        <rect width="25.4816" height="25.4816" fill="white" transform="translate(0.72168 0.36377)" />
      </clipPath>
      <clipPath id="clip1_415_24189">
        <rect width="25.4816" height="25.4816" fill="white" transform="translate(0.72168 0.36377)" />
      </clipPath>
    </defs>
  </svg>
);

const DecreasingBoxArrow = () => (
  <svg width="27" height="26" viewBox="0 0 27 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_415_24204)">
      <path
        d="M23.6569 0.682251H3.27163C2.04024 0.682251 1.04199 1.6805 1.04199 2.91189V23.2972C1.04199 24.5286 2.04024 25.5268 3.27163 25.5268H23.6569C24.8883 25.5268 25.8866 24.5286 25.8866 23.2972V2.91189C25.8866 1.6805 24.8883 0.682251 23.6569 0.682251Z"
        fill="#7CF1C7"
      />
      <path
        d="M23.6569 0.682251H3.27163C2.04024 0.682251 1.04199 1.6805 1.04199 2.91189V23.2972C1.04199 24.5286 2.04024 25.5268 3.27163 25.5268H23.6569C24.8883 25.5268 25.8866 24.5286 25.8866 23.2972V2.91189C25.8866 1.6805 24.8883 0.682251 23.6569 0.682251Z"
        stroke="#7CF1C7"
        strokeWidth="0.63704"
      />
      <mask
        id="mask0_415_24204"
        style={{ maskType: 'luminance' }}
        maskUnits="userSpaceOnUse"
        x="8"
        y="8"
        width="11"
        height="10"
      >
        <path d="M8.68652 8.32703H18.2421V17.8826H8.68652V8.32703Z" fill="white" />
      </mask>
      <g mask="url(#mask0_415_24204)">
        <path
          d="M13.4447 8.99615V17.2356M13.4447 17.2356L15.6629 15.0175M13.4447 17.2356L11.2266 15.0175"
          stroke="#183344"
          strokeWidth="1.27408"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </g>
    <defs>
      <clipPath id="clip0_415_24204">
        <rect width="25.4816" height="25.4816" fill="white" transform="translate(0.724609 0.36377)" />
      </clipPath>
    </defs>
  </svg>
);

// Risk Factor specific types and helpers
export type RiskFactor = {
  name: string;
  direction: 'increasing' | 'decreasing' | 'neutral';
};

const createDirectionalArrow = (direction: RiskFactor['direction']) => {
  if (direction === 'neutral') return null;

  return direction === 'increasing' ? <IncreasingBoxArrow /> : <DecreasingBoxArrow />;
};

const getRiskFactorBackgroundColor = (direction: RiskFactor['direction']) => {
  switch (direction) {
    case 'increasing':
      return '#fef2f2'; // red-50
    case 'decreasing':
      return '#f0fdf4'; // green-50
    case 'neutral':
      return '#f9fafb'; // gray-50
    default:
      return '#f9fafb';
  }
};

// Helper function to convert risk factors to pill marquee items
export const riskFactorsToPillItems = (riskFactors: RiskFactor[]): PillMarqueeItem[] => {
  return riskFactors.map((factor) => ({
    text: factor.name,
    icon: createDirectionalArrow(factor.direction),
  }));
};

// TODO: drive from backend/Sanity
const defaultRiskFactors: RiskFactor[] = [
  { name: 'Smoking', direction: 'increasing' },
  { name: 'Regular Exercise', direction: 'decreasing' },
  { name: 'Family History', direction: 'increasing' },
  { name: 'Healthy Diet', direction: 'decreasing' },
  { name: 'Age', direction: 'increasing' },
  { name: 'Stress Management', direction: 'decreasing' },
  { name: 'Alcohol Consumption', direction: 'increasing' },
  { name: 'Regular Check-ups', direction: 'decreasing' },
  { name: 'Environmental Toxins', direction: 'increasing' },
  { name: 'Adequate Sleep', direction: 'decreasing' },
  { name: 'UV Exposure', direction: 'increasing' },
  { name: 'Vaccination', direction: 'decreasing' },
];

// Risk-specific component using the generic PillMarquee
interface RiskMarqueeProps {
  riskFactors?: RiskFactor[];
  className?: string;
  'data-content-type'?: ContentType;
}

export const RiskMarquee = ({ riskFactors = defaultRiskFactors, className = '', ...props }: RiskMarqueeProps) => {
  const pillItems = riskFactorsToPillItems(riskFactors);

  return (
    <PillMarquee
      items={pillItems}
      pillStyles={{ backgroundColor: getRiskFactorBackgroundColor('neutral'), color: 'black' }}
      className={className}
      {...props}
    />
  );
};
